/* eslint-disable no-nested-ternary */
import React, { useContext } from "react";

import classNames from "classnames";
import { observer } from "mobx-react";
import { lighten } from "polished";
import { useForm } from "react-hook-form";
import styled from "styled-components";

import JobAlertForm from "../../../components/JobAlertForm/JobAlertForm";
import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import breakpoints from "../../constants";
import Markdown from "../../styles/Markdown";
import Button from "../Button/Button";

const StyledContainer = styled.div`
  width: 100%;

  @media (min-width: ${breakpoints.DESKTOP}) {
    height: ${({ containerHeight }) =>
      containerHeight ? `${containerHeight} !important` : "auto"};
    background-position-x: ${({ $backgroundPosition }) => $backgroundPosition};
    background-image: ${({ backgroundImage }) =>
      backgroundImage ? `url(${backgroundImage})` : null};
    background-repeat: no-repeat;
    background-size: ${({ backgroundSize }) => backgroundSize || "contain"};
  }
  &:before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: ${({ overlay }) => overlay || null};
  }
`;

const StyledWrapper = styled.div`
  color: ${({ textColor, theme }) =>
    textColor === "negative"
      ? theme.negativeTextColor
      : textColor === "primary"
      ? theme.textColor
      : textColor};

  transform: translate(
    ${({ xOffset, yOffset }) => `${xOffset || "0"}, ${yOffset || "0"}`}
  );
  background-color: ${({ innerBackgroundColor }) =>
    innerBackgroundColor || "#fff"};

  @media (min-width: ${breakpoints.DESKTOP}) {
    max-width: ${({ innerWidth }) => innerWidth || "495px"};
  }
`;

const StyledSignUp = styled.div`
  height: ${({ height }) => height || "80px"};
  max-width: 670px;
  font-size: ${({ inputFontSize }) => inputFontSize || "13px"};

  input {
    height: 100%;
    border: none;
  }
`;

const StyledButtonWrapper = styled.div`
  width: ${({ buttonWidth }) => buttonWidth};
  height: 100%;
`;

const StyledButton = styled(Button)`
  height: 100%;
  width: 100%;
  background-color: ${({ theme, buttonColour }) =>
    buttonColour || theme.primaryColor};
  color: ${({ theme, buttonTextColour }) =>
    buttonTextColour || theme.negativeTextColor};
  border: none;
  outline: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  &:disabled {
    background-color: ${({ buttonColour, theme }) =>
      lighten(0.3, buttonColour || theme.primaryColor)};
  }

  &:hover,
  &:active,
  &:focus {
    background-color: ${({ buttonHoverColour, theme }) =>
      lighten(0.1, buttonHoverColour || theme.primaryColor)};
  }
`;

function StandardJobAlert() {
  const { register, handleSubmit } = useForm();
  const { modalStore } = useStores();
  const { openModal } = modalStore;

  const {
    backgroundImage,
    backgroundPosition,
    buttonWidth = "120px",
    containerHeight,
    backgroundSize,
    formPosition = "center",
    innerBackgroundColor = "transparent",
    innerWidth,
    inputFontSize = "14px",
    inputHeight,
    text: jobAlertText,
    textColor = "primary",
    xOffset = "0",
    yOffset = "0",
    agreementContent,
    privacyPolicyLink,
    formHeaderBackgroundColour,
    overlay,
    buttonText,
    placeholderText,
    buttonColour,
    buttonTextColour,
    buttonHoverColour,
  } = useContext(ComponentContext);

  const onSubmit = (data) => {
    openModal({
      content: (
        <JobAlertForm
          agreementContent={agreementContent}
          emailToSend={data.email}
          privacyPolicyLink={privacyPolicyLink}
          formHeaderBackgroundColour={formHeaderBackgroundColour}
        />
      ),
      size: "md",
    });
  };

  const horizontalAlignmentMap = {
    center: "center",
    left: "start",
    right: "end",
  };

  const containerClassName = classNames(
    { [`align-items-${horizontalAlignmentMap[formPosition]}`]: true },
    "d-flex flex-column justify-content-center",
    "position-relative",
    "py-3 py-lg-0"
  );

  return (
    <StyledContainer
      backgroundImage={`${backgroundImage?.url || ""}`}
      $backgroundPosition={backgroundPosition}
      backgroundSize={backgroundSize}
      className={containerClassName}
      containerHeight={containerHeight}
      data-testid="job-alert-container"
      overlay={overlay}
    >
      <StyledWrapper
        className="p-3 p-md-5 w-100 job-alert-wrapper"
        innerBackgroundColor={innerBackgroundColor}
        innerWidth={innerWidth}
        textColor={textColor}
        xOffset={xOffset}
        yOffset={yOffset}
      >
        {jobAlertText && <Markdown>{jobAlertText}</Markdown>}

        <StyledSignUp
          className="d-flex"
          height={inputHeight}
          inputFontSize={inputFontSize}
        >
          <form
            className="w-100 d-flex align-items-center"
            onSubmit={handleSubmit(onSubmit)}
          >
            <label className="visually-hidden" htmlFor="emailInput">
              {placeholderText || "Email address"}
            </label>
            <input
              aria-label="Email address"
              className="px-3 flex-grow-1"
              id="emailInput"
              placeholder={placeholderText || "Email address"}
              type="text"
              {...register("email")}
            />
            <StyledButtonWrapper buttonWidth={buttonWidth}>
              <StyledButton
                className="text-uppercase"
                height="100%"
                type="submit"
                buttonColour={buttonColour}
                buttonTextColour={buttonTextColour}
                buttonHoverColour={buttonHoverColour}
              >
                {buttonText || "Sign Up"}
              </StyledButton>
            </StyledButtonWrapper>
          </form>
        </StyledSignUp>
      </StyledWrapper>
    </StyledContainer>
  );
}

export default observer(StandardJobAlert);
